// FILE: client/src/components/images/Uploader.jsx

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { useDropzone } from "react-dropzone";
import imageCompression from "browser-image-compression";
import { toast } from "react-toastify";
import { Button, Spinner, Alert } from "react-bootstrap";
import { deleteImage, uploadImages } from "../../actions/uploadActions";
import { getApiErrorMessage } from "../../utils/errorMessages";
import { normalizeProductImage } from "../../utils/productImages";
import ProductImage from "./ProductImage";

const normalizeImageArray = (images = []) => {
  const raw = Array.isArray(images) ? images : images ? [images] : [];

  return raw.map((image) => normalizeProductImage(image)).filter(Boolean);
};

const STANDARD_IMAGE_COMPRESSION_OPTIONS = Object.freeze({
  maxSizeMB: 0.5,
  maxWidthOrHeight: 1600,
  useWebWorker: true,
});

const LARGE_PHOTO_COMPRESSION_OPTIONS = Object.freeze({
  maxSizeMB: 1.5,
  maxWidthOrHeight: 2400,
  useWebWorker: true,
});

const ORIGINAL_UPLOAD_LIMIT_BYTES = 20 * 1024 * 1024;

const imageKey = (img) => `${img.public_id || "local"}::${img.url}`;

const safeText = (value = "") => String(value ?? "").trim();

function getCompressionOptions(folder = "") {
  const normalizedFolder = safeText(folder).toLowerCase();

  if (
    normalizedFolder === "about" ||
    normalizedFolder === "credits" ||
    normalizedFolder === "jewelry-hire" ||
    normalizedFolder === "portfolio" ||
    normalizedFolder.startsWith("portfolio/")
  ) {
    return LARGE_PHOTO_COMPRESSION_OPTIONS;
  }

  return STANDARD_IMAGE_COMPRESSION_OPTIONS;
}

const isDebugApiEnabled = () =>
  safeText(process.env.REACT_APP_DEBUG_API).toLowerCase() === "true";

const getSafeErrorDebug = (error = {}) => ({
  code: safeText(error?.code || error?.errorCode || error?.safeDebug?.code),
  route: safeText(error?.safeDebug?.route),
  status: Number(error?.status || error?.statusCode || 0) || undefined,
  requestId: safeText(error?.requestId),
});

const logSafeError = (label, error) => {
  if (!isDebugApiEnabled()) return;
  console.error(label, getSafeErrorDebug(error));
};

const getErrorMessage = (
  error,
  fallback = "We could not upload your images right now. Please try again later.",
) => getApiErrorMessage(error, { fallback });

const Uploader = ({
  setImageUrls,
  existingImages = [],
  folder = "products",
  maxImages,
}) => {
  const dispatch = useDispatch();
  const normalizedFolder = safeText(folder).toLowerCase();
  const isProductUpload =
    normalizedFolder === "products" || normalizedFolder.startsWith("products/");
  const isServerProcessedUpload =
    isProductUpload ||
    normalizedFolder === "jewelry-hire" ||
    normalizedFolder.startsWith("jewelry-hire/") ||
    normalizedFolder === "portfolio" ||
    normalizedFolder.startsWith("portfolio/");
  const compressionOptions = getCompressionOptions(folder);
  const imageLimit =
    Number.isInteger(maxImages) && maxImages > 0 ? maxImages : null;

  const [images, setImages] = useState(() =>
    normalizeImageArray(existingImages),
  );
  const [previewFiles, setPreviewFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const previewFilesRef = useRef([]);

  useEffect(() => {
    setImages(normalizeImageArray(existingImages));
  }, [existingImages]);

  useEffect(() => {
    previewFilesRef.current = previewFiles;
  }, [previewFiles]);

  useEffect(() => {
    return () => {
      previewFilesRef.current.forEach((item) => {
        if (item?.previewUrl) {
          URL.revokeObjectURL(item.previewUrl);
        }
      });
    };
  }, []);

  const syncImages = (next) => {
    setImages(next);
    setImageUrls?.(next);
  };

  const clearPreviews = () => {
    setPreviewFiles((prev) => {
      prev.forEach((item) => {
        if (item?.previewUrl) {
          URL.revokeObjectURL(item.previewUrl);
        }
      });

      return [];
    });
  };

  const handleFiles = useCallback(async (incomingFiles) => {
    const allowed = ["jpeg", "jpg", "png", "webp", "avif"];

    const valid = incomingFiles.filter((file) => {
      const ext = file.name.split(".").pop()?.toLowerCase();

      return allowed.includes(ext);
    });

    if (!valid.length) {
      toast.error("Only JPG, PNG, WEBP, or AVIF images are supported.");
      return;
    }

    const remainingSlots = imageLimit
      ? Math.max(0, imageLimit - images.length - previewFiles.length)
      : valid.length;

    if (remainingSlots === 0) {
      toast.warning(`Only ${imageLimit} image can be uploaded here.`);
      return;
    }

    if (valid.length > remainingSlots) {
      toast.warning(`Only ${imageLimit} image can be uploaded here.`);
    }

    const filesToPrepare = valid.slice(0, remainingSlots);

    try {
      const previews = await Promise.all(
        filesToPrepare.map(async (file) => {
          const shouldPreserveOriginal =
            isServerProcessedUpload &&
            file.size <= ORIGINAL_UPLOAD_LIMIT_BYTES;

          const preparedFile = shouldPreserveOriginal
            ? file
            : await imageCompression(file, compressionOptions);

          return {
            file: preparedFile,
            previewUrl: URL.createObjectURL(preparedFile),
          };
        }),
      );

      setPreviewFiles((prev) => [...prev, ...previews]);
    } catch (err) {
      logSafeError("Image preparation failed", err);
      toast.error(
        "We could not prepare those images. Try smaller JPG, PNG, WEBP, or AVIF files.",
      );
    }
  }, [
    compressionOptions,
    imageLimit,
    images.length,
    isServerProcessedUpload,
    previewFiles.length,
  ]);

  const onDrop = useCallback((acceptedFiles) => {
    void handleFiles(acceptedFiles);
  }, [handleFiles]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/*": [".jpg", ".jpeg", ".png", ".webp", ".avif"],
    },
    multiple: imageLimit !== 1,
  });

  const handleUpload = async () => {
    if (!previewFiles.length) return;

    setUploading(true);
    setUploadError("");

    try {
      const filesToUpload = previewFiles.map((item) => item.file);
      const uploaded = await dispatch(uploadImages(filesToUpload, folder));
      const normalizedUploaded = normalizeImageArray(uploaded);
      const updated = [...images, ...normalizedUploaded];

      syncImages(updated);
      clearPreviews();

      toast.success("Images uploaded successfully");
    } catch (err) {
      logSafeError("Image upload failed", err);

      const message = getErrorMessage(err);

      setUploadError(message);
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  const removePreview = (index) => {
    setPreviewFiles((prev) => {
      const next = [...prev];
      const removed = next[index];

      if (removed?.previewUrl) {
        URL.revokeObjectURL(removed.previewUrl);
      }

      next.splice(index, 1);

      return next;
    });
  };

  const handleDelete = async (targetImage) => {
    try {
      if (targetImage?.public_id) {
        await dispatch(deleteImage(targetImage));
      }

      const updated = images.filter(
        (img) => imageKey(img) !== imageKey(targetImage),
      );

      syncImages(updated);

      toast.success("Image removed");
    } catch (err) {
      logSafeError("Image delete failed", err);
      toast.error(
        getErrorMessage(
          err,
          "We could not delete that image right now. Please try again later.",
        ),
      );
    }
  };

  const moveImage = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= images.length) return;

    const updated = [...images];
    const [moved] = updated.splice(fromIndex, 1);

    updated.splice(toIndex, 0, moved);

    syncImages(updated);
  };

  return (
    <div className="uploader mt-3">
      {uploadError ? <Alert variant="danger">{uploadError}</Alert> : null}

      <div
        {...getRootProps()}
        className={`dropzone border rounded p-3 text-center ${
          isDragActive ? "bg-light border-primary" : "bg-white"
        }`}
        style={{ cursor: "pointer" }}
      >
        <input {...getInputProps()} />

        {isDragActive ? (
          <p className="mb-0">Drop images here…</p>
        ) : (
          <div>
            <p className="mb-1">
              Drag and drop {imageLimit === 1 ? "an image" : "images"}, or
              click to browse
            </p>

            <small className="text-muted">
              {imageLimit === 1
                ? "JPG, PNG, WEBP, AVIF · one image only"
                : "JPG, PNG, WEBP, AVIF · first image is used as the cover"}
            </small>
          </div>
        )}
      </div>

      {previewFiles.length > 0 && (
        <div className="mt-3">
          <strong>Ready to upload ({previewFiles.length})</strong>

          <div className="d-flex flex-wrap gap-2 mt-2">
            {previewFiles.map((item, idx) => (
              <div key={idx} style={{ position: "relative", width: 100 }}>
                <img
                  src={item.previewUrl}
                  alt={`preview-${idx}`}
                  style={{
                    width: 100,
                    height: 100,
                    objectFit: isProductUpload ? "contain" : "cover",
                    borderRadius: 6,
                    boxShadow: "0 0 4px rgba(0,0,0,0.1)",
                  }}
                />

                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => removePreview(idx)}
                  style={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    padding: "2px 6px",
                    fontSize: "0.75rem",
                  }}
                >
                  ×
                </Button>
              </div>
            ))}
          </div>

          <Button
            variant="primary"
            className="mt-2"
            onClick={handleUpload}
            disabled={uploading}
          >
            {uploading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Uploading...
              </>
            ) : (
              "Upload selected images"
            )}
          </Button>
        </div>
      )}

      {images.length > 0 && (
        <div className="mt-4">
          <strong>Uploaded images ({images.length})</strong>

          <div className="d-flex flex-wrap gap-3 mt-2">
            {images.map((img, idx) => (
              <div key={imageKey(img)} style={{ width: 120 }}>
                <div style={{ position: "relative" }}>
                  <ProductImage
                    image={img}
                    alt={img.alt || `uploaded-${idx}`}
                    sizes="120px"
                    style={{
                      width: 120,
                      height: 120,
                      objectFit: isProductUpload ? "contain" : "cover",
                      borderRadius: 6,
                      boxShadow: "0 0 4px rgba(0,0,0,0.1)",
                    }}
                  />

                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDelete(img)}
                    style={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      padding: "2px 6px",
                      fontSize: "0.75rem",
                    }}
                  >
                    ×
                  </Button>
                </div>

                <div className="d-flex gap-1 mt-2">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    disabled={idx === 0}
                    onClick={() => moveImage(idx, idx - 1)}
                  >
                    ↑
                  </Button>

                  <Button
                    size="sm"
                    variant="outline-secondary"
                    disabled={idx === images.length - 1}
                    onClick={() => moveImage(idx, idx + 1)}
                  >
                    ↓
                  </Button>
                </div>

                {idx === 0 && (
                  <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                    Cover image
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Uploader;
